import { api } from '@/lib/api-client'
import type {
Classification,
Category,
Product,
SaleConfig,
SaleConfigPayload,
PricingScheme,
UnitOfMeasurement,
UomWrapper,
GeoapifyFeature,
} from '@/types'

/**
 * Response shape for geo-autocomplete API.
 */
export interface GeoAutocompleteResponse {
  features: GeoapifyFeature[]
}

/**
 * Fetch address suggestions from geo-autocomplete service.
 * @param text - Search text
 * @returns Promise resolving to geo-autocomplete response
 */
export async function getGeoAutocomplete(text: string): Promise<GeoAutocompleteResponse> {
  // Routed through the same-origin `/api/geoapify` proxy (see vite.config.ts /
  // the prod reverse proxy) so the browser never makes a CORS-dependent
  // cross-origin request to the Geoapify worker.
  return api.get<GeoAutocompleteResponse>('/api/geoapify', { params: { text } })
}

/**
 * Generic paginated data wrapper for API responses.
 */
export interface PaginatedData<T> {
  data: T[]
  meta: {
    total: number
    page: number
    limit: number
    totalPages: number
  }
}

/**
 * Query parameters for paginated list requests.
 */
export interface PaginationParams {
  page?: number
  limit?: number
  search?: string
}

/**
 * Query parameters for fetching categories by classification.
 */
export interface CategoryParams extends PaginationParams {
  classificationId?: string
}

/**
 * Query parameters for fetching products.
 */
export interface ProductParams extends PaginationParams {
  brandId?: string
  categoryId?: string
  classificationId?: string
  isActive?: boolean
}

/**
 * Payload for creating a new product.
 */
export interface CreateProductPayload {
  name: string
  compactName: string
  categoryId: string
  brandId?: string
  classificationId?: string
  description?: string
  highlights?: string
  images?: string[]
  isCannabis?: boolean
  isActive?: boolean
  tagIds?: string[]
  thcValue?: string | number | null
  thcUnit?: 'PERCENTAGE' | 'MG' | '%' | null
  cbdValue?: string | number | null
  cbdUnit?: 'PERCENTAGE' | 'MG' | '%' | null
}

/**
 * Payload for updating an existing product.
 */
export interface UpdateProductPayload extends Partial<CreateProductPayload> {
  isSuspended?: boolean
  suspendReason?: string
}

/**
 * Fetch paginated classifications.
 * @param params - Query parameters (page, limit, search)
 * @returns Promise resolving to paginated classifications
 */
export async function getClassifications(
  params?: PaginationParams
): Promise<PaginatedData<Classification>> {
  return api.get<PaginatedData<Classification>>('/classifications', { params })
}

/**
 * Fetch categories, optionally filtered by classification.
 * @param params - Query parameters including optional classificationId
 * @returns Promise resolving to paginated categories
 */
export async function getCategories(
  params?: CategoryParams
): Promise<PaginatedData<Category>> {
  return api.get<PaginatedData<Category>>('/categories', { params })
}

/**
 * Fetch paginated products with optional filters.
 * @param params - Query parameters (page, limit, search, filters)
 * @returns Promise resolving to paginated products
 */
export async function getProducts(
  params?: ProductParams
): Promise<PaginatedData<Product>> {
  return api.get<PaginatedData<Product>>('/products', { params })
}

/**
 * Fetch a single product by ID.
 * @param id - Product ID
 * @returns Promise resolving to product details
 */
export async function getProduct(id: string): Promise<Product> {
  return api.get<Product>(`/products/${id}`)
}

/**
 * Create a new product.
 * @param data - Product creation payload
 * @returns Promise resolving to the created product
 */
export async function createProduct(data: CreateProductPayload): Promise<Product> {
  return api.post<Product>('/products', data)
}

/**
 * Update an existing product.
 * @param id - Product ID
 * @param data - Product update payload
 * @returns Promise resolving to the updated product
 */
export async function updateProduct(
  id: string,
  data: UpdateProductPayload
): Promise<Product> {
  return api.patch<Product>(`/products/${id}`, data)
}

/**
 * Delete a product by ID.
 * @param id - Product ID
 * @returns Promise resolving when deletion is complete
 */
export async function deleteProduct(id: string): Promise<void> {
  return api.delete<void>(`/products/${id}`)
}

/**
 * Fetch sale configs for a specific product.
 * @param productId - Product ID
 * @returns Promise resolving to array of sale configs
 */
export async function getProductSaleConfigs(
  productId: string
): Promise<SaleConfig[]> {
  const response = await api.get<SaleConfig[] | { data?: SaleConfig[] }>(`/sale-configs/product/${productId}`)
  return Array.isArray(response) ? response : (response.data ?? [])
}

/**
 * Fetch a single sale config by ID with pricing scheme and tiers.
 * @param id - Sale config ID
 * @returns Promise resolving to the sale config detail
 */
export async function getSaleConfig(id: string): Promise<SaleConfig> {
  // Safe assertion: api.get unwraps axios response; backend returns { data: SaleConfig } envelope
  const res = await api.get<{ data: SaleConfig }>(`/sale-configs/${id}`, {
    params: { includePricingScheme: true, includeTiers: true }
  })
  return (res?.data || res) as SaleConfig
}

/**
 * Fetch multiple sale configs by their IDs in a single request (avoids N+1).
 * @param ids - Array of sale config IDs to fetch
 * @returns Promise resolving to a record mapping ID → SaleConfig
 */
export async function getSaleConfigsByIds(ids: string[]): Promise<Record<string, SaleConfig>> {
  if (ids.length === 0) return {}
  const res = await api.get<SaleConfig[]>('/sale-configs', {
    params: { ids: ids.join(',') },
  })
  const list = (Array.isArray(res) ? res : ((res as { data?: SaleConfig[] })?.data ?? [])) as SaleConfig[]
  return Object.fromEntries(list.map((sc) => [sc.id, sc]))
}

/**
 * Create a new sale config for a product.
 * @param data - Sale config creation payload
 * @returns Promise resolving to the created sale config
 */
export async function createSaleConfig(
  data: SaleConfigPayload
): Promise<SaleConfig> {
  return api.post<SaleConfig>('/sale-configs', data)
}

/**
 * Update an existing sale config.
 * @param id - Sale config ID
 * @param data - Sale config update payload
 * @returns Promise resolving to the updated sale config
 */
export async function updateSaleConfig(
  id: string,
  data: Partial<SaleConfigPayload>
): Promise<SaleConfig> {
  return api.patch<SaleConfig>(`/sale-configs/${id}`, data)
}

/**
 * Delete a sale config by ID.
 * @param id - Sale config ID
 * @returns Promise resolving when deletion is complete
 */
export async function deleteSaleConfig(id: string): Promise<void> {
  return api.delete<void>(`/sale-configs/${id}`)
}

/**
 * Fetch all pricing schemes (excluding system-managed).
 * @returns Promise resolving to array of pricing schemes
 */
export async function getPricingSchemes(): Promise<PricingScheme[]> {
  // Safe assertion: api.get unwraps axios response; backend returns { data: PricingScheme[] } or plain array
  const res = await api.get<{ data: PricingScheme[] }>('/pricing-schemes', {
    params: { includeSystemManaged: false },
  })
  return (res?.data || res || []) as PricingScheme[]
}

/**
 * Fetch UOMs assigned to a specific category.
 * The category UOM endpoint may return wrapped objects ({ uom: UnitOfMeasurement }) or plain UOMs.
 * @param categoryId - Category ID
 * @returns Promise resolving to array of UnitOfMeasurement
 */
export async function getCategoryUoms(categoryId: string): Promise<UnitOfMeasurement[]> {
  const res = await api.get<(UomWrapper | UnitOfMeasurement)[]>(`/categories/${categoryId}/uoms`)
  // Safe assertion: response is either plain array or { data: [...] } envelope
  const raw = (res as unknown as { data?: (UomWrapper | UnitOfMeasurement)[] })?.data || res || []
  return Array.isArray(raw)
    ? raw.map((item) =>
        'uom' in item && item.uom && typeof item.uom === 'object' ? item.uom : item as UnitOfMeasurement
      )
    : []
}

/**
 * Fetch all global UOMs (no category filter).
 * @returns Promise resolving to array of UnitOfMeasurement
 */
export async function getAllUoms(): Promise<UnitOfMeasurement[]> {
  const res = await api.get<(UomWrapper | UnitOfMeasurement)[]>('/units-of-measurement')
  // Safe assertion: response is either plain array or { data: [...] } envelope
  const raw = (res as unknown as { data?: (UomWrapper | UnitOfMeasurement)[] })?.data || res || []
  return Array.isArray(raw)
    ? raw.map((item) =>
        'uom' in item && item.uom && typeof item.uom === 'object' ? item.uom : item as UnitOfMeasurement
      )
    : []
}
